import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/api';

interface Theme {
  slug: string;
  name: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  is_premium?: boolean;
}

interface ThemeContextType {
  currentTheme: Theme | null;
  themes: Theme[];
  setTheme: (slug: string) => Promise<void>;
  refreshThemes: () => Promise<void>;
  loading: boolean;
}

const defaultTheme: Theme = {
  slug: 'purple',
  name: 'Purple Dream',
  primary_color: '#667eea',
  secondary_color: '#764ba2',
  accent_color: '#9f7aea'
};

// All available themes with colors (fallback if API fails)
const fallbackThemes: Theme[] = [
  { slug: 'purple', name: 'Purple Dream', primary_color: '#667eea', secondary_color: '#764ba2', accent_color: '#9f7aea' },
  { slug: 'teal', name: 'Ocean Teal', primary_color: '#0d9488', secondary_color: '#14b8a6', accent_color: '#5eead4' },
  { slug: 'pink', name: 'Rose Pink', primary_color: '#ec4899', secondary_color: '#f472b6', accent_color: '#fbcfe8' },
  { slug: 'orange', name: 'Sunset Orange', primary_color: '#f97316', secondary_color: '#fb923c', accent_color: '#fed7aa', is_premium: true },
  { slug: 'dark', name: 'Midnight Dark', primary_color: '#1f2937', secondary_color: '#374151', accent_color: '#6b7280', is_premium: true }
];

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: defaultTheme,
  themes: fallbackThemes,
  setTheme: async () => {},
  refreshThemes: async () => {},
  loading: true
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(defaultTheme);
  const [themes, setThemes] = useState<Theme[]>(fallbackThemes);
  const [loading, setLoading] = useState(true);

  // Apply theme CSS variables
  const applyTheme = useCallback((theme: Theme) => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primary_color);
    root.style.setProperty('--secondary-color', theme.secondary_color);
    root.style.setProperty('--accent-color', theme.accent_color || theme.primary_color);
    root.style.setProperty('--gradient-start', theme.primary_color);
    root.style.setProperty('--gradient-end', theme.secondary_color);
    
    // Generate light version of primary color (add transparency)
    root.style.setProperty('--primary-light', `${theme.primary_color}15`);
    root.style.setProperty('--primary-dark', theme.secondary_color);
    
    // Save to localStorage for persistence
    localStorage.setItem('selected_theme', JSON.stringify(theme));
    
    console.log('Theme applied:', theme.name);
  }, []);

  // Load saved theme from localStorage on initial mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('selected_theme');
    if (savedTheme) {
      try {
        const theme = JSON.parse(savedTheme);
        setCurrentTheme(theme);
        applyTheme(theme);
      } catch (e) {
        applyTheme(defaultTheme);
      }
    } else {
      applyTheme(defaultTheme);
    }
    setLoading(false);
  }, [applyTheme]);

  // Fetch themes from server
  const refreshThemes = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return;
      }

      const response = await apiService.getAvailableThemes();
      if (response.themes && response.themes.length > 0) {
        setThemes(response.themes);
        
        // Find and apply user's current theme
        const userThemeSlug = response.currentTheme || response.defaultTheme || 'purple';
        const theme = response.themes.find((t: Theme) => t.slug === userThemeSlug);
        
        if (theme) {
          setCurrentTheme(theme);
          applyTheme(theme);
        }
      }
    } catch (err) {
      console.log('Theme fetch error, using cached/default');
    }
  }, [applyTheme]);

  // Fetch themes when auth token changes
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        refreshThemes();
      }
    };

    // Initial fetch
    refreshThemes();

    // Listen for login/logout
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshThemes]);

  const setTheme = async (slug: string) => {
    // Find theme in list
    const theme = themes.find(t => t.slug === slug) || fallbackThemes.find(t => t.slug === slug);
    
    if (theme) {
      // Apply immediately for instant feedback
      setCurrentTheme(theme);
      applyTheme(theme);
      
      // Then save to server
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          await apiService.updateUserTheme(slug);
        }
      } catch (err) {
        console.error('Failed to save theme to server:', err);
        // Theme is already applied locally, so user still sees the change
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, themes, setTheme, refreshThemes, loading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
